import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp,
  CheckCircle2,
  AlertCircle,
  PlusCircle,
  MinusCircle,
  ChevronRight,
  ArrowRight,
  Sparkles,
} from 'lucide-react';
import SyncCardCompactModule from '../../Common/components/common/SyncCardCompact';
import ProcessCardModule from '../../Common/components/common/ProcessCard';
import { useToast } from '../../Common/context/ToastContext';
import axios from 'axios';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts';

const SyncCardCompact = typeof SyncCardCompactModule === 'function' ? SyncCardCompactModule : (SyncCardCompactModule?.default ?? SyncCardCompactModule);
const ProcessCard = typeof ProcessCardModule === 'function' ? ProcessCardModule : (ProcessCardModule?.default ?? ProcessCardModule);

// 대시보드: API 장애 시 사용되는 고정 목업 데이터
const MOCK_TODAY_STATS = {
  todayAmount: 0,
  yesterdayAmount: 0,
  diffAmount: 0,
  diffRate: 0,
};

const MOCK_WEEKLY_ROWS = [
  { label: '월', amount: 0 },
  { label: '화', amount: 0 },
  { label: '수', amount: 0 },
  { label: '목', amount: 0 },
  { label: '금', amount: 0 },
  { label: '토', amount: 0 },
  { label: '일', amount: 0 },
];

const MOCK_LATEST_SYNC = {
  started_at: '2026-02-02 16:25:00',
  finished_at: '2026-02-02 16:25:00',
  status: 'SUCCESS',
  total_count: 0,
  inserted_count: 0,
  updated_count: 0,
  inactivated_count: 0,
  error_count: 0,
};

/**
 * 관리자 대시보드 - example.js 전체 이식
 */
function Dashboard() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const showToastRef = useRef(showToast);
  showToastRef.current = showToast;

  // 금일/전일 매출 요약
  const [todayStats, setTodayStats] = useState(MOCK_TODAY_STATS);
  // 주간 매출 추이 (레이블 + 금액)
  const [weeklyRows, setWeeklyRows] = useState(MOCK_WEEKLY_ROWS);
  // 월간 매출 추이 (YYYY-MM / 금액)
  const [monthlyRows, setMonthlyRows] = useState([]);
  const [settleSummary, setSettleSummary] = useState(null);
  const [latestSync] = useState(MOCK_LATEST_SYNC);

  const [aiInsight, setAiInsight] = useState('');
  const [isAiLoading, setIsAiLoading] = useState(false);
  // 관리자 대시보드 요약 조회
  useEffect(() => {
    const fetchSummary = async () => {
      const baseUrl = process.env.REACT_APP_API || '';
      try {
        const { data } = await axios.get(
          baseUrl + '/api/admin/dashboard/summary',
          { withCredentials: true }
        );
        if (data?.code !== 1000 || !data?.data) return;

        const payload = data.data;

        // 1) 금일/전일 매출 + 증감
        const today = Number(payload.todayTotal || 0);
        const yesterday = Number(payload.yesterdayTotal || 0);
        const delta = Number(payload.delta || today - yesterday);
        const diffRate =
          yesterday > 0 ? (delta / yesterday) * 100 : today > 0 ? 100 : 0;

        setTodayStats({
          todayAmount: today,
          yesterdayAmount: yesterday,
          diffAmount: delta,
          diffRate,
        });

        // 2) 주간 매출 추이: [{ date: 'YYYY-MM-DD', total }]
        if (Array.isArray(payload.weeklyTrend)) {
          const weekly = payload.weeklyTrend.map((row) => ({
            label: row.date
              ? row.date.slice(5).replace('-', '/') // '02-01' -> '02/01'
              : '',
            amount: Number(row.total || 0),
          }));
          setWeeklyRows(weekly);
        }

        // 3) 월간 매출 추이: [{ month: 'YYYY-MM', total }]
        if (Array.isArray(payload.monthlyTrend)) {
          const monthly = payload.monthlyTrend.map((row) => ({
            label: row.month
              ? row.month.replace('-', '년 ') + '월' // '2026-02' -> '2026년 02월'
              : '',
            amount: Number(row.total || 0),
          }));
          setMonthlyRows(monthly);
        }
      } catch (err) {
        // 실패 시 목업 데이터 유지, 필요하면 토스트로만 알림
        const msg =
          err?.response?.data?.msg ||
          '대시보드 요약 조회 중 오류가 발생했습니다.';
        showToastRef.current(msg);
      }
    };

    fetchSummary();
  }, []);

  // 오늘 정산 관련 요약 조회 (승인금액/정산확정/입금완료)
  useEffect(() => {
    const baseUrl = process.env.REACT_APP_API || '';

    const fetchTodaySettleStats = async () => {
      try {
        const { data } = await axios.get(
          baseUrl + '/api/admin/dashboard/today-stats',
          { withCredentials: true },
        );
        if (data?.code !== 1000 || !data?.data) return;
        const payload = data.data;
        setSettleSummary({
          approvedAmount: Number(payload.approvedAmount || 0),
          settlementConfirmedAmount: Number(
            payload.settlementConfirmedAmount || 0,
          ),
          transferredAmount: Number(payload.transferredAmount || 0),
        });
      } catch (err) {
        // 실패 시에는 목업 데이터를 기반으로만 표시
        console.error('fetch today-stats failed', err);
      }
    };

    fetchTodaySettleStats();
  }, []);

  const formatCurrency = (n) =>
    Number(n || 0).toLocaleString('ko-KR') + '원';

  const diffIsPositive = todayStats.diffAmount >= 0;
  const diffLabel =
    todayStats.diffAmount === 0
      ? '0원 (0.0%)'
      : `${diffIsPositive ? '+' : ''}${Math.abs(
          todayStats.diffAmount,
        ).toLocaleString('ko-KR')}원 (${diffIsPositive ? '+' : ''}${todayStats.diffRate.toFixed(
          1,
        )}%)`;

  const todayLabel = (() => {
    const now = new Date();
    return `${now.getFullYear()}년 ${now.getMonth() + 1}월 ${now.getDate()}일`;
  })();

  const settleCards = (() => {
    const fallback = {
      approvedAmount: 14205000,
      settlementConfirmedAmount: 13920900,
      transferredAmount: 22104000,
    };
    const src = settleSummary || fallback;
    const gross = Number(src.approvedAmount || 0); // 오늘 승인금액
    const net = Number(src.settlementConfirmedAmount || 0); // 정산확정 금액
    const transfer = Number(src.transferredAmount || 0); // 입금완료 금액
    return {
      gross: gross.toLocaleString('ko-KR') + '원',
      net: net.toLocaleString('ko-KR') + '원',
      transfer: transfer.toLocaleString('ko-KR') + '원',
    };
  })();

  const latestSyncLabel = (() => {
    if (!latestSync) return '동기화 이력 없음';

    const raw =
      latestSync.finished_at && String(latestSync.finished_at).trim()
        ? latestSync.finished_at
        : latestSync.started_at && String(latestSync.started_at).trim()
        ? latestSync.started_at
        : '';

    if (!raw) return '동기화 이력 없음';

    // 공백 기준으로 날짜/시간 분리 (예: "2026-02-03 14:20:00")
    const [d, t] = String(raw).split(' ');
    const dateStr = d ? d.replace(/-/g, '.') : '';

    const rawStatus = String(latestSync.status || '').toUpperCase();
    let statusLabel = '';
    if (rawStatus.includes('SUCCESS') || rawStatus === 'DONE') statusLabel = '성공';
    else if (rawStatus.includes('FAIL')) statusLabel = '실패';
    else if (rawStatus) statusLabel = rawStatus;

    return `${dateStr} ${t || ''}${statusLabel ? ` (${statusLabel})` : ''}`;
  })();

  const syncStats = (() => {
    if (!latestSync) {
      return {
        total: '-',
        inserted: '-',
        inactivated: '-',
        error: '-',
      };
    }
    const total = Number(latestSync.total_count ?? 0);
    const inserted = Number(latestSync.inserted_count ?? 0);
    const inactivated = Number(latestSync.inactivated_count ?? 0);

    let error =
      latestSync.error_count != null
        ? Number(latestSync.error_count)
        : 0;

    const statusUpper = String(latestSync.status || '').toUpperCase();
    if (!error && statusUpper.includes('FAIL')) {
      error = 1;
    }

    return {
      total: total.toLocaleString('ko-KR'),
      inserted: (inserted > 0 ? '+' : '') + inserted.toLocaleString('ko-KR'),
      inactivated:
        (inactivated > 0 ? '-' : '') + inactivated.toLocaleString('ko-KR'),
      error: error.toLocaleString('ko-KR'),
    };
  })();

  const generateAiInsight = async () => {
    setIsAiLoading(true);
    try {
      setAiInsight('데이터 분석 중...');
      await new Promise((r) => setTimeout(r, 1500));
      setAiInsight('현재 정산 데이터 기준으로 이상 징후는 발견되지 않았습니다. 매출 추이는 전주 대비 +2.4% 상승했으며, 정산 처리율은 98.5%로 정상 범위입니다.');
      showToast('데이터 분석이 완료되었습니다.');
    } catch (e) {
      setAiInsight('AI 분석 로딩 중 오류가 발생했습니다.');
    } finally {
      setIsAiLoading(false);
    }
  };

  return (
    <div className="flex-1 min-h-0 py-5 px-8 overflow-auto space-y-4">
      {/* 금일 매출 */}
      <section className="pb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-[18px] font-bold text-[#191f28] leading-[42px]">
            금일 매출 ({todayLabel})
          </h2>
          <div className="flex items-center gap-3">
            <span className="text-[22px] font-black text-[#191f28]">
              {formatCurrency(todayStats.todayAmount)}
            </span>
            {/* <div
              className={`flex items-center gap-1 font-bold text-[16px] ${
                todayStats.diffAmount === 0
                  ? 'text-[#6b7280]'
                  : diffIsPositive
                  ? 'text-[var(--brand-primary)]'
                  : 'text-[#dc2626]'
              }`}
            >
              {todayStats.diffAmount === 0 ? null : (
                <TrendingUp
                  size={16}
                  className={diffIsPositive ? '' : 'rotate-180'}
                />
              )}
              <span>{diffLabel}</span>
            </div> */}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-6">
          <div className="bg-white rounded p-4 flex flex-col border border-[#e0e0e0] shadow-md">
            <span className="text-[18px] font-bold text-[#8b95a1] mb-1">
              주간 매출 추이
            </span>
            {weeklyRows.length > 0 && (
              <span className="text-[12px] text-[#9ca3af] mb-2">
                {weeklyRows[0].label} ~{' '}
                {weeklyRows[weeklyRows.length - 1].label}
              </span>
            )}
            <div className="flex-1 relative px-1 pb-1">
              {weeklyRows.length === 0 ? (
                <div className="text-[14px] text-[#adb5bd]">
                  주간 매출 데이터가 없습니다.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={180}>
                  <LineChart
                    data={weeklyRows}
                    margin={{ top: 10, right: 10, left: 0, bottom: 10 }}
                  >
                    <CartesianGrid stroke="#f3f4f6" vertical={false} />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 11, fill: '#9ca3af' }}
                      tickFormatter={(label) => {
                        if (!label) return '';
                        const parts = String(label).split(/[\/\-]/);
                        const day = parts[parts.length - 1] || '';
                        return day ? `${parseInt(day, 10)}일` : '';
                      }}
                      axisLine={false}
                      tickLine={false}
                      padding={{ left: 0, right: 0 }}
                    />
                    <YAxis hide />
                    <RechartsTooltip
                      formatter={(value) => [
                        `${Number(value || 0).toLocaleString('ko-KR')}원`,
                        '매출',
                      ]}
                      labelFormatter={(label) => label}
                    />
                    <Line
                      type="monotone"
                      dataKey="amount"
                      stroke="var(--brand-primary)"
                      strokeWidth={3}
                      dot={{ r: 3 }}
                      activeDot={{ r: 5 }}
                      connectNulls
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
          <div className="bg-white rounded p-4 flex flex-col border border-[#e0e0e0] shadow-md">
            <span className="text-[18px] font-bold text-[#4e5968] mb-1">
              월간 매출 추이
            </span>
            {monthlyRows.length > 0 && (
              <span className="text-[12px] text-[#9ca3af] mb-2">
                {monthlyRows[0].label} ~{' '}
                {monthlyRows[monthlyRows.length - 1].label}
              </span>
            )}
            <div className="flex-1 relative px-1 pb-1">
              {monthlyRows.length === 0 ? (
                <div className="text-[14px] text-[#adb5bd]">
                  월간 매출 데이터가 없습니다.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={180}>
                  <LineChart
                    data={monthlyRows}
                    margin={{ top: 10, right: 10, left: 0, bottom: 10 }}
                  >
                    <CartesianGrid stroke="#f3f4f6" vertical={false} />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 11, fill: '#9ca3af' }}
                      tickFormatter={(label) => {
                        if (!label) return '';
                        const match = String(label).match(/(\d{1,2})월/);
                        const m = match ? parseInt(match[1], 10) : null;
                        return m != null ? `${m}월` : label;
                      }}
                      axisLine={false}
                      tickLine={false}
                      padding={{ left: 0, right: 0 }}
                    />
                    <YAxis hide />
                    <RechartsTooltip
                      formatter={(value) => [
                        `${Number(value || 0).toLocaleString('ko-KR')}원`,
                        '매출',
                      ]}
                      labelFormatter={(label) => label}
                    />
                    <Line
                      type="monotone"
                      dataKey="amount"
                      stroke="var(--brand-primary)"
                      strokeWidth={3}
                      dot={{ r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* 정산 */}
      <section className="pb-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <h2 className="text-[18px] font-bold text-[#191f28]">정산</h2>
            {/* 요약 통계는 기간 필터 없이 summary 기준으로 표시 */}
          </div>
          <button 
            onClick={() => navigate('/admin/settlement')}
            className="text-[16px] font-bold text-[#adb5bd] flex items-center gap-1 hover:text-[#191f28]"
          >
            전체 보기 <ChevronRight size={18} />
          </button>
        </div>
        {aiInsight && (
          <div className="mb-4 p-4 bg-[var(--brand-primary)]/5 border border-[var(--brand-primary)]/20 rounded-xl">
            <p className="text-[16px] leading-relaxed text-[#4e5968]">{aiInsight}</p>
          </div>
        )}
        <div className="grid grid-cols-3 gap-6 relative">
          <ProcessCard
            label="승인금액"
            amount={settleCards?.gross ?? '0원'}
            // count="summary 기준"
            color="var(--brand-primary)"
            status="정상"
          />
          <ProcessCard
            label="정산확정"
            amount={settleCards?.net ?? '0원'}
            // count="summary 기준"
            color="#3182f6"
            status="처리중"
          />
          <ProcessCard
            label="입금완료"
            amount={settleCards?.transfer ?? '0원'}
            // count="summary 기준"
            color="var(--brand-primary)"
            status="성공"
          />
          {/* <div className="absolute left-[31%] top-1/2 -translate-y-1/2 hidden lg:block text-[#e5e8eb]">
            <ArrowRight size={24} />
          </div>
          <div className="absolute left-[64.5%] top-1/2 -translate-y-1/2 hidden lg:block text-[#e5e8eb]">
            <ArrowRight size={24} />
          </div> */}
        </div>
      </section>

      {/*
        약국 데이터 동기화 현황 섹션 (현재 미사용)
        - UI 및 navigate('/admin/pharmacies') 이동, SyncCardCompact 카드 전체 주석 처리
        - 실제 약국 동기화 API 연동 시 다시 활성화 예정

      <section className="pb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h2 className="text-[18px] font-bold text-[#191f28]">
              약국 데이터 동기화 현황
            </h2>
            <span className="text-[16px] text-[#8b95a1] font-mono">
              {latestSyncLabel}
            </span>
          </div>
          <button 
            onClick={() => navigate('/admin/pharmacies')}
            className="text-[16px] font-bold text-[#adb5bd] flex items-center gap-1 hover:text-[#191f28]"
          >
            전체 보기 <ChevronRight size={18} />
          </button>
        </div>
        <div className="grid grid-cols-4 gap-4">
          <SyncCardCompact
            label="전체 약국"
            value={syncStats.total}
            unit="곳"
            icon={<CheckCircle2 size={18} className="text-[var(--brand-primary)]" />}
          />
          <SyncCardCompact
            label="신규 영업"
            value={syncStats.inserted}
            unit="곳"
            icon={<PlusCircle size={18} className="text-[var(--brand-primary)]" />}
            color="var(--brand-primary)"
          />
          <SyncCardCompact
            label="폐업 처리"
            value={syncStats.inactivated}
            unit="곳"
            icon={<MinusCircle size={18} className="text-[#f04452]" />}
            color="#f04452"
          />
          <SyncCardCompact
            label="동기화 오류"
            value={syncStats.error}
            unit="건"
            icon={<AlertCircle size={18} className="text-[#adb5bd]" />}
          />
        </div>
      </section>
      */}
    </div>
  );
}

export default Dashboard;
